import { Inject, Injectable, InjectionToken } from '@angular/core';
import gitInfo from '../../../../../src/git-version.json';
import * as Sentry from '@sentry/angular-ivy';
import { appVersion } from '../../../../../src/version';
import { UserRoleEnum } from '@evermile/configuration-manager';

// GTM service
// declare the dataLayer to use in typescript
declare let dataLayer: any[];
declare let window: any;

export const anonymousUserName = 'Anonymous';

export const IS_PROD_ENV = new InjectionToken<boolean>('isProdEnv');

@Injectable({
  providedIn: 'root',
})
export class GtmTracking {
  isProdEnv = true;

  merchantSubscriptionTier?: string;
  userRole?: UserRoleEnum;

  constructor(@Inject(IS_PROD_ENV) isProdEnv: boolean) {
    this.isProdEnv = isProdEnv;
  }

  registerView(url: string, type?: string, sessionScope?: any): void {
    const user = this.getGtmUser();

    this.preDataLayerPush({
      event: 'page_view',
      pageScope: {
        url: url,
        type: type ? type : url,
        title: document.title,
        website_version: '0.0.3',
      },
      userScope: user,
      sessionScope: sessionScope,
    });
  }

  invalidForm(formName: string, formValue: any, formErrorField: any): void {
    const user = this.getGtmUser();

    this.preDataLayerPush({
      event: 'invalid',
      hitScope: {
        formName: formName,
        formData: formValue,
        formError: formErrorField,
      },
      userScope: user,
    });
    Sentry.withScope(function (scope) {
      scope.setExtras(formValue);
      scope.setExtra('formErrorField', formErrorField);
      Sentry.captureMessage(formName);
    });
  }

  submitForm(formName: string, formValue: any): void {
    const user = this.getGtmUser();

    this.preDataLayerPush({
      event: 'submit',
      hitScope: {
        formName: formName,
        formData: formValue,
      },
      userScope: user,
    });
    Sentry.withScope(function (scope) {
      scope.setLevel('info');
      scope.setExtras(formValue);
      Sentry.captureMessage(formName);
    });
  }

  registerContentView(formName: string, formValue: any): void {
    const user = this.getGtmUser();

    this.preDataLayerPush({
      event: 'content_view',
      hitScope: {
        formName: formName,
        formData: formValue,
      },
      userScope: user,
    });
  }

  clickEvent(eventParentAndComponentNames: string): void {
    const user = this.getGtmUser();

    this.preDataLayerPush({
      event: 'click',
      hitScope: {
        elementName: eventParentAndComponentNames,
      },
      userScope: user,
    });
  }

  preDataLayerPush(event: any) {
    const user = this.getGtmUser();
    if (!this.isProdEnv) {
      console.log(event);
    }
    // If Sudo Mode
    if (user.original_merchant_id || user.isAdmin) {
      return;
    }
    window.preDataLayer = window.preDataLayer || [];
    event.event = 'data.' + event.event;
    event.version_hash = gitInfo.hash;
    event.version_time = gitInfo.time;
    event.version = appVersion;
    window.preDataLayer.push(event);
  }

  getGtmUser() {
    const user = JSON.parse(localStorage.getItem('gtmUser') || `{"name": "${anonymousUserName}"}`);
    if (this.merchantSubscriptionTier) {
      user.merchantSubscriptionPlan = this.merchantSubscriptionTier;
    }
    if (this.userRole) {
      user.role = this.userRole;
    }
    return user;
  }

  setMerchantSubscriptionTier(tier?: string) {
    this.merchantSubscriptionTier = tier;
  }

  setUserRole(role?: UserRoleEnum) {
    this.userRole = role;
  }
}
